// Recipe stubs — lightweight dish-concept catalog.
//
// A STUB is just enough metadata to know "this is a thing people
// might cook" — title, blurb, key canonical ingredients, discovery
// tags, build hint. NOT a full recipe (no timers, per-step uses,
// reheat block, etc.). The full recipe is generated on-demand via
// src/lib/cookStub.js when a user actually wants to cook it.
//
// Stubs power situation-aware recommendations:
//   - "It's almost July 4th. Try Classic Cheeseburger for your cookout."
//   - "It's Sunday morning. How about Shakshuka for brunch?"
//   - "Cold front rolling in — Chicken Noodle Soup sounds right."
//
// See:
//   tags.js                  — discovery taxonomy + date helpers
//   src/lib/occasionContext.js — current-occasion engine
//   src/lib/useRecommendedStubs.js — ranking + dedupe
//   src/lib/cookStub.js     — community-or-AI build adapter

import { BRUNCH_STUBS } from "./brunch";
import { COLD_WEATHER_STUBS } from "./cold-weather";
import { COOKOUT_STUBS } from "./cookout";
import { DATE_NIGHT_STUBS } from "./date-night";
import { HOLIDAY_STUBS } from "./holidays";
import { KIDS_STUBS } from "./kids";
import { SUNDAY_MORNING_STUBS } from "./sunday-morning";
import { SUPERBOWL_STUBS } from "./superbowl";
import { WEEKNIGHT_STUBS } from "./weeknight";

export { DISCOVERY_TAGS, DATE_HELPERS } from "./tags";

// Flat catalog. Per-bucket files are organizational only — once
// flattened, a stub's reachability is purely through its
// `discoveryTags` array, not which file it lives in. The same
// recipe (e.g. spaghetti-bolognese) can be in `weeknight.js` AND
// also fly a `kids` tag for the kid-friendly surfacing.
export const RECIPE_STUBS = [
  ...BRUNCH_STUBS,
  ...COLD_WEATHER_STUBS,
  ...COOKOUT_STUBS,
  ...DATE_NIGHT_STUBS,
  ...HOLIDAY_STUBS,
  ...KIDS_STUBS,
  ...SUNDAY_MORNING_STUBS,
  ...SUPERBOWL_STUBS,
  ...WEEKNIGHT_STUBS,
];

// Build a Map id → stub once at module load. findStub is hot in
// recommendation rendering + graduation lookups.
const STUB_BY_ID = new Map(RECIPE_STUBS.map((s) => [s.id, s]));

export function findStub(id) {
  if (!id) return null;
  return STUB_BY_ID.get(id) || null;
}

// All stubs that fly a given discovery tag.
export function stubsByTag(tagId) {
  if (!tagId) return [];
  return RECIPE_STUBS.filter(
    (s) => Array.isArray(s.discoveryTags) && s.discoveryTags.includes(tagId),
  );
}

// All stubs whose keyIngredients include a given canonical id.
// Used by pantry-driven recommendation paths ("you have ground_beef
// — here's three things you could make").
export function stubsByCanonical(canonicalId) {
  if (!canonicalId) return [];
  return RECIPE_STUBS.filter(
    (s) => Array.isArray(s.keyIngredients) && s.keyIngredients.includes(canonicalId),
  );
}
